import React, { use, useEffect, useRef, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import ActivityTable from '../../../components/tables/ActivityTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useActivityApi } from '../../../hooks/useActivityApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete, showAlert } from '../../../utils/sweetAlertUtils';
import {
  ActivityCategoryDTO,
  ActivityDTO,
  ActivityStatusDTO,
  ActivityTypeDTO,
  EmailMessageDTO,
  MemberDTO,
  MemberFirearmDTO,
  NoteDTO,
} from '../../../services/apiClient';
import { useLocation, useNavigate } from 'react-router-dom';
import { useActivityTypes } from '../../../hooks/useActivityTypeApi';
import { useActivityCategories } from '../../../hooks/useActivityCategoryApi';
import { useMemberFirearmApi } from '../../../hooks/useMemberFirearmApi';
import { useActivityStatuses } from '../../../hooks/useActivityStatusApi';
import { getMemberId, getRoles } from '../../../utils/tokenUtils';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';

import { useViewMode } from '../../../utils/viewModeUtils/useViewMode';
import { ViewToggle } from '../../../components/common/ViewToggle';
import { ViewModeConstants } from '../../../constants/ViewModeConstants';
import MemberActivityDataCard from '../../../components/dataCards/MemberActivityDataCard';
import { FileViewer } from '../../../components/common/FileViewer';
import ComplianceOverview from '../../../components/dashboards/ComplianceOverview';
import { useMemberApi } from '../../../hooks/useMemberApi';
import ComplianceHistoryOverview from '../../../components/dashboards/ComplianceHistoryOverview';
import { isDedicatedCategory } from '../../../utils/complianceUtils';
import { getButtonLegendItems } from '../../../utils/buttonLegendDisplayUtils';
import ComplianceHistoryFull from '../../../components/drawer/ComplianceHistoryFull';
import Drawer from '../../../components/common/Drawer';
import FeedbackForm from '../../../components/forms/FeedbackForm';
import { ComponentConstants } from '../../../constants/ComponentConstants';
import { ActivityStatusConstants } from '../../../constants/ActivityStatusConstants';
import { DrawerViewConstants, ActivityDrawerViews } from '../../../constants/DrawerViewConstants';
import EmailDrawerContent from '../../../components/common/EmailDrawerContent';
import { useEmailMessageApi } from '../../../hooks/useEmailMessageApi';
import { useEmailHandlers } from '../../../hooks/useEmailHandlers';
import { useNoteApi } from '../../../hooks/useNoteApi';
import AddNote from '../../../components/drawer/AddNote';
import ViewNoteHistory from '../../../components/drawer/ViewNoteHistory';
import MemberQuickDetails from '../../../components/drawer/MemberQuickDetails';
import GeneralSearch from '../../../components/common/GeneralSearch';
import { useImpersonation } from '../../../hooks/useImpersonation';
import { ChevronDown } from 'lucide-react';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';
import ButtonLegendCta from '../../../components/common/ButtonLegendCta';

const ActivityList: React.FC = () => {
  const roles = getRoles();
  const memberId = getMemberId();
  const { search, remove, restoreFromDeleted, extract, updateStatus, getDistinctYears, isLoading, error } = useActivityApi();
  const [items, setItems] = useState<ActivityDTO[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    showAdvanced,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSort,
    setSearchFilters,
    setShowAdvanced,
    commitSearch,
    clearSearchState,
  } = useSearchState('activities', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });

  const [totalRecords, setTotalRecords] = useState(0);
  const [showActivityReportButton, setShowActivityReportButton] = useState(false);
  const [defaultStatusId, setDefaultStatusId] = useState<string>();
  const [selectedActivity, setSelectedActivity] = useState<ActivityDTO>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedActivityIds, setSelectedActivityIds] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState<boolean>(false);
  const bulkActionsRef = useRef<HTMLDivElement>(null);
  const { sendEmail, isLoading: isSendingEmail } = useEmailMessageApi();
  const { create: createNote, isLoading: isSubmittingNote } = useNoteApi();
  const { impersonate } = useImpersonation();

  const statusFieldName = 'activityStatusId';

  const { view, toggle } = useViewMode(
    'member-activities',
    roles?.includes('User') ? ViewModeConstants.DATA_CARDS : ViewModeConstants.TABLE,
  );
  const [viewingFile, setViewingFile] = useState<{
    name: string;
    url: string;
  } | null>(null);

  const [activityCategories, setActivityCategories] = useState<ActivityCategoryDTO[]>([]);
  const [member, setMember] = useState<MemberDTO | null>(null);

  const legendItems = getButtonLegendItems('activity', {
    showFullHistory: !!(member && isDedicatedCategory(member.memberCategoryId!)),
  });

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerKey, setDrawerKey] = useState(0);
  const [drawerView, setDrawerView] = useState<ActivityDrawerViews>();
  const [drawerTite, setDrawerTitle] = useState<string>('');
  const [drawerSize, setDrawerSize] = useState<'small' | 'large'>('large');

  const navigate = useNavigate();
  const location = useLocation();

  const statusFromUrl = location.pathname.includes('/status/')
    ? location.pathname.split('/status/')[1].toLowerCase()
    : '';

  // Clear items when route variant changes (prevents showing stale data from previous page)
  const previousPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (previousPathnameRef.current !== location.pathname) {
      setItems([]); // Clear old data immediately
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Function to get the correct filters based on status
  const getFiltersFromStatus = (status: string) => {
    if (memberId) return { isDeleted: false };
    switch (status.toLowerCase()) {
      case 'deleted':
        return { isDeleted: true };
      default:
        return { isDeleted: false };
    }
  };

  // Function to get display name for status
  const getStatusDisplayName = (status: string) => {
    switch (status.toLowerCase()) {
      case 'deleted':
        return 'Deleted';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const { data: activityStatusesData = [], isLoading: isLoadingActivityStatuses } = useActivityStatuses();
  const { data: activityTypesData = [], isLoading: isLoadingActivityTypes } = useActivityTypes();
  const { data: activityCategoriesData = [], isLoading: isLoadingActivityCategories } = useActivityCategories();
  const { search: searchMemberFirearms, isLoading: isLoadingMemberFirearms } =
    useMemberFirearmApi();
  const { getById } = useMemberApi();

  const { edit, getReport } = useActivityApi();

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Activities', href: '#' },
  ];

  useEffect(() => {
    // Set initial filters based on URL status
    if (statusFromUrl) {
      const statusFilters = getFiltersFromStatus(statusFromUrl);
      setSearchFilters((prev: any) => ({
        ...prev,
        ...statusFilters,
      }));
    }
  }, [statusFromUrl]);

  // Reset then reload years when route changes
  useEffect(() => {
    setSelectedYear(null);
    setAvailableYears([]);
    if (!roles?.includes('User')) return;
    getDistinctYears().then((years) => {
      setAvailableYears(years);
      if (years.length > 0) setSelectedYear(years[0]);
    });
  }, [statusFromUrl]);



  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const statusFilters = getFiltersFromStatus(statusFromUrl);
      const yearFilters = selectedYear
        ? {
          createdFrom: new Date(selectedYear, 0, 1).toISOString(),
          createdTo: new Date(selectedYear, 11, 31, 23, 59, 59).toISOString(),
        }
        : {};
      const response = await search({
        filters: {
          ...filters,
          ...statusFilters,
          ...yearFilters,
        },
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      setItems(response?.data?.results || []);
      ``;
      setTotalRecords(response?.data?.totalCount || 0);

      if (roles?.includes('User')) {
        // check if any record has status Approved
        const hasApproved = response?.data?.results!.some((row: ActivityDTO) =>
          row.activityStatus?.title?.toLowerCase().includes('approved'),
        );
        setShowActivityReportButton(hasApproved!);
      }
      return response?.data?.totalCount || 0;
    } catch (error) {
      console.error('Error fetching Activities:', error);
      return 0;
    }
  };

  const previousFiltersRef = useRef<string>('');
  const isCommittingSearchRef = useRef<boolean>(false);
  const yearInitialisedRef = useRef<boolean>(false);

  // Refetch when selected year changes
  useEffect(() => {
    if (selectedYear === null) {
      if (yearInitialisedRef.current) {
        yearInitialisedRef.current = false;
        setCurrentPage(1);
        fetchItems(1, pageSize, searchFilters);
      }
      return;
    }
    yearInitialisedRef.current = true;
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // Sync check: get current URL filters on every render to detect mismatches
  const getCurrentUrlFilters = () => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};
    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });
    return JSON.stringify(urlFilters);
  };

  const currentUrlFiltersKey = getCurrentUrlFilters();
  const areFiltersStale = items.length > 0 && previousFiltersRef.current !== currentUrlFiltersKey;

  // Replace the useEffect that watches for page changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const urlFilters: Record<string, string> = {};

    params.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        urlFilters[key] = value;
      }
    });

    const currentFiltersKey = JSON.stringify(urlFilters);

    // Skip this effect if we're in the middle of a commitSearch AND the URL hasn't actually changed yet
    if (isCommittingSearchRef.current && currentFiltersKey === previousFiltersRef.current) {
      return;
    }

    // URL has now updated, clear the flag
    if (isCommittingSearchRef.current) {
      isCommittingSearchRef.current = false;
    }

    // Parse page and pageSize from URL (source of truth) to avoid stale state
    const urlPage = params.get('page');
    const urlPageSize = params.get('pageSize');
    const page = urlPage ? parseInt(urlPage, 10) : 1;
    const size = urlPageSize ? parseInt(urlPageSize, 10) : pageSize;

    // If URL has no filter params but searchFilters state has some, clear the state
    // This handles the case where user navigates between routes and searchFilters is stale
    const hasUrlFilters = Object.keys(urlFilters).length > 0;
    const hasStateFilters = searchFilters && Object.keys(searchFilters).length > 0;
    if (!hasUrlFilters && hasStateFilters) {
      setSearchFilters({});
    }

    // Clear items immediately if filters changed (not just page number)
    if (previousFiltersRef.current !== currentFiltersKey) {
      setItems([]);
      previousFiltersRef.current = currentFiltersKey;
    }

    fetchItems(page, size, {
      ...urlFilters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, statusFromUrl]);

  const fetchMember = async () => {
    try {
      const response = await getById(memberId!);
      setMember(response?.data || null);
    } catch (error) {
      console.error('Error fetching Member Firearms', error);
    }
  };

  useEffect(() => {
    fetchMember();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, {
      ...searchFilters,
    });
  };

  const handleMemberClick = (memberId: string) => {
    const activity = items.find((item) => item.memberId === memberId);
    const idPassportNumber = activity?.member?.idPassportNumber || '';

    // Set flag to prevent intermediate useEffect from running with stale URL
    isCommittingSearchRef.current = true;

    // Clear other filters and search only by ID number, while pushing browser history.
    const newFilters = idPassportNumber ? { idPassportNumber } : {};
    // The useEffect watching location.search will trigger the fetch automatically
    commitSearch(newFilters, { page: 1, showAdvanced: false, replace: false });
  };

  const handleClearSearch = () => {
    clearSearchState();
    fetchItems(1, pageSize, {});
  };

  // Email handlers from useEmailHandlers hook
  const {
    handleSendEmail,
    handleSendEmailSubmit,
    handleViewAllEmails,
    handleViewRecordEmails,
  } = useEmailHandlers<ActivityDTO>({
    setSelectedItem: setSelectedActivity,
    setDrawerTitle,
    setDrawerView,
    setIsDrawerOpen,
    sendEmail,
    fetchItems: async (page: number, size: number, filters: any) => {
      await fetchItems(page, size, filters);
    },
    currentPage,
    pageSize,
    searchFilters,
  });

  const handleDelete = async (dto: ActivityDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting Activity:', error);
    }
  };

  const handleRestore = async (activity: ActivityDTO) => {
    try {
      await restoreFromDeleted(activity.id ?? '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error restoring activity:', error);
    }
  };

  const handleOpenDrawer = async (
    activity: ActivityDTO,
    drawerView: ActivityDrawerViews,
    drawerTitle: string,
    drawerSize: 'small' | 'large' = 'large',
    defaultStatusId?: string,
  ) => {
    setSelectedActivity(activity);
    setDrawerView(drawerView);
    setDrawerTitle(drawerTitle);
    setDrawerSize(drawerSize);
    if (defaultStatusId) setDefaultStatusId(defaultStatusId);
    setDrawerKey((k) => k + 1);
    setIsDrawerOpen(true);
  };

  const handleAddNote = async (data: NoteDTO) => {
    try {
      setIsSubmitting(true);
      await createNote(data);
      setIsDrawerOpen(false);
      setSelectedActivity({});
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error creating note: ', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await extract({
        filters: searchFilters,
        page: 1,
        pageSize: 9999999, // Large number to export all matching records
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting Activities:', error);
    }
  };

  const handleActivityReport = async () => {
    try {
      const response = await getReport(memberId!);
      if (response?.success && response.data) {
        setViewingFile({
          name: `Activity-Report.pdf`,
          url: response.data,
        });
      }
    } catch (error) {
      console.error('Error generating report:', error);
    }
  };

  const handleImpersonateMemberLogin = async (dto: ActivityDTO): Promise<void> => {
    await impersonate(dto.memberId, dto.member?.memberStatusId);
  };

  // Drawer handlers
  const handleFullHistoryClick = () => {
    setDrawerView(DrawerViewConstants.FULL_HISTORY);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setDrawerView(undefined);
    setSelectedActivity({});
    setDefaultStatusId(undefined);
    setShowBulkActions(false);
    setDrawerSize('large');
  };

  const handleStatusSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);

      // Determine which activity IDs to update
      const idsToUpdate = selectedActivityIds.size > 0
        ? Array.from(selectedActivityIds)
        : [selectedActivity.id!];

      // Prepare bulk update DTO
      const updateDto = {
        recordIds: idsToUpdate,
        activityStatusId: data.activityStatusId,
        feedback: data.feedback,
        replyTemplateId: data.replyTemplateId,
        sendFeedbackEmail: data.sendFeedbackEmail || false,
      };

      await updateStatus(updateDto);

      setIsDrawerOpen(false);
      setSelectedActivity({});
      setSelectedActivityIds(new Set());
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      await showAlert({
        icon: 'error',
        title: 'Update Failed',
        text: 'An error occurred while updating the activity status. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSort(sortKey, !sortDescending);
    } else {
      setSort(sortKey, true);
    }
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);

    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);

    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    setSelectedActivityIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = items.map(item => item.id).filter((id): id is string => !!id);
      setSelectedActivityIds(new Set(allIds));
    } else {
      setSelectedActivityIds(new Set());
    }
  };

  const handleBulkStatusUpdate = () => {
    const firstSelectedId = Array.from(selectedActivityIds)[0];
    const firstActivity = items.find(a => a.id === firstSelectedId) || {};
    const count = selectedActivityIds.size;
    handleOpenDrawer(
      firstActivity as ActivityDTO,
      DrawerViewConstants.STATUS_CHANGE,
      `Update Status (${count} activit${count > 1 ? 'ies' : 'y'})`
    );
  };

  const handleBulkApprove = () => {
    const firstSelectedId = Array.from(selectedActivityIds)[0];
    const firstActivity = items.find(a => a.id === firstSelectedId) || {};
    const count = selectedActivityIds.size;
    handleOpenDrawer(
      firstActivity as ActivityDTO,
      DrawerViewConstants.STATUS_CHANGE,
      `Approve (${count} activit${count > 1 ? 'ies' : 'y'})`,
      'large',
      ActivityStatusConstants.APPROVED
    );
  };

  const handleBulkApproveWithFeedback = () => {
    const firstSelectedId = Array.from(selectedActivityIds)[0];
    const firstActivity = items.find(a => a.id === firstSelectedId) || {};
    const count = selectedActivityIds.size;
    handleOpenDrawer(
      firstActivity as ActivityDTO,
      DrawerViewConstants.STATUS_CHANGE,
      `Approve with Feedback (${count} activit${count > 1 ? 'ies' : 'y'})`,
      'large',
      ActivityStatusConstants.APPROVED_WITH_FEEDBACK
    );
  };

  const handleBulkAcknowledge = () => {
    const firstSelectedId = Array.from(selectedActivityIds)[0];
    const firstActivity = items.find(a => a.id === firstSelectedId) || {};
    const count = selectedActivityIds.size;
    handleOpenDrawer(
      firstActivity as ActivityDTO,
      DrawerViewConstants.STATUS_CHANGE,
      `Acknowledge (${count} activit${count > 1 ? 'ies' : 'y'})`,
      'large',
      ActivityStatusConstants.ACKNOWLEDGED
    );
  };

  const handleBulkReviewWithFeedback = () => {
    const firstSelectedId = Array.from(selectedActivityIds)[0];
    const firstActivity = items.find(a => a.id === firstSelectedId) || {};
    const count = selectedActivityIds.size;
    handleOpenDrawer(
      firstActivity as ActivityDTO,
      DrawerViewConstants.STATUS_CHANGE,
      `Review with Feedback (${count} activit${count > 1 ? 'ies' : 'y'})`,
      'large',
      ActivityStatusConstants.REVIEWED_WITH_FEEDBACK
    );
  };

  const handleBulkDecline = () => {
    const firstSelectedId = Array.from(selectedActivityIds)[0];
    const firstActivity = items.find(a => a.id === firstSelectedId) || {};
    const count = selectedActivityIds.size;
    handleOpenDrawer(
      firstActivity as ActivityDTO,
      DrawerViewConstants.STATUS_CHANGE,
      `Decline (${count} activit${count > 1 ? 'ies' : 'y'})`,
      'large',
      ActivityStatusConstants.DECLINED
    );
  };

  const handleBulkOutstanding = () => {
    const firstSelectedId = Array.from(selectedActivityIds)[0];
    const firstActivity = items.find(a => a.id === firstSelectedId) || {};
    const count = selectedActivityIds.size;
    handleOpenDrawer(
      firstActivity as ActivityDTO,
      DrawerViewConstants.STATUS_CHANGE,
      `Mark Outstanding (${count} activit${count > 1 ? 'ies' : 'y'})`,
      'large',
      ActivityStatusConstants.OUTSTANDING
    );
  };

  // Close bulk actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bulkActionsRef.current && !bulkActionsRef.current.contains(event.target as Node)) {
        setShowBulkActions(false);
      }
    };

    if (showBulkActions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showBulkActions]);

  return (
    <AdminLayout
      activeMenuItem={
        statusFromUrl ? `/admin/activities/status/${statusFromUrl}` : '/admin/activities'
      }
      breadcrumbs={[
        { label: 'Admin', href: '#' },
        { label: 'Activities', href: '#' },
        ...(statusFromUrl
          ? [
            {
              label: getStatusDisplayName(statusFromUrl),
              href: '#',
            },
          ]
          : []),
      ]}
      allowedRoles={['Admin', 'User']}
      requiredPermission="Manage.Activity.View"
    >
      <Card
        title={`Manage Activities ${statusFromUrl ? `- ${getStatusDisplayName(statusFromUrl)}` : ''}`}
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex items-center space-x-2">
            {roles?.includes('Admin') && selectedActivityIds.size > 0 && (
              <div ref={bulkActionsRef} className="relative">
                <Button
                  variant="secondary"
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className="flex items-center gap-2"
                >
                  Bulk Actions
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full">
                    {selectedActivityIds.size}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${showBulkActions ? 'rotate-180' : ''
                      }`}
                  />
                </Button>
                {showBulkActions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                    <button
                      onClick={() => {
                        handleBulkStatusUpdate();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg"
                    >
                      Update Status
                    </button>
                    <button
                      onClick={() => {
                        handleBulkApprove();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        handleBulkApproveWithFeedback();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Approve with Feedback
                    </button>
                    <button
                      onClick={() => {
                        handleBulkAcknowledge();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Acknowledge
                    </button>
                    <button
                      onClick={() => {
                        handleBulkReviewWithFeedback();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Review with Feedback
                    </button>
                    <button
                      onClick={() => {
                        handleBulkDecline();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Decline
                    </button>
                    <button
                      onClick={() => {
                        handleBulkOutstanding();
                        setShowBulkActions(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 last:rounded-b-lg"
                    >
                      Mark Outstanding
                    </button>
                  </div>
                )}
              </div>
            )}
            {roles?.includes('Admin') && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowAdvanced(!showAdvanced);
                  }}
                >
                  {showAdvanced ? 'Basic Search' : 'Advanced Search'}
                </Button>
                <Button type="button" variant="secondary" onClick={handleExport}>
                  Export
                </Button>
              </>
            )}

            {roles?.includes('User') && (
              <>
                {showActivityReportButton && (
                  <Button type="button" variant="secondary" onClick={handleActivityReport}>
                    Activity Report
                  </Button>
                )}
                <ButtonLegendCta items={legendItems} />
                <AddNewButton label='Submit an Activity' onClick={() => navigate('/admin/activities/create')} />
              </>
            )}
          </div>
        }
      >
        {roles?.includes('User') && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="lg:col-span-1">{member && <ComplianceOverview member={member} />}</div>
            <div className="lg:col-span-2">
              {member && (
                <ComplianceHistoryOverview
                  member={member}
                  onFullHistoryClick={handleFullHistoryClick}
                />
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSearch} className="mb-4">
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <FormField
                id="idPassportNumber"
                label="Search by ID Number"
                value={searchFilters.idPassportNumber || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    idPassportNumber: e.target.value,
                  })
                }
                placeholder="Search by ID Number"
              />
              <FormField
                id="activityStatusId"
                label="Search by Activity Status"
                value={searchFilters.activityStatusId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    activityStatusId: e.target.value,
                  })
                }
                placeholder="Select Activity Status"
                options={activityStatusesData.map((type: ActivityStatusDTO) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingActivityStatuses}
              />
              <FormField
                id="activityTypeId"
                label="Search by Activity Type"
                value={searchFilters.activityTypeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    activityTypeId: e.target.value,
                  })
                }
                placeholder="Select Activity Type"
                options={activityTypesData.map((type) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingActivityTypes}
              />
              <FormField
                id="activityCategoryId"
                label="Search by Activity Category"
                value={searchFilters.activityCategoryId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    activityCategoryId: e.target.value,
                  })
                }
                placeholder="Select Activity Category"
                options={activityCategoriesData.map((type: ActivityCategoryDTO) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingActivityCategories}
              />
              <FormField
                id="activityDate"
                label="Search by Activity Date"
                value={searchFilters.activityDate || ''}
                type="date"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    activityDate: e.target.value,
                  })
                }
                placeholder="Search by Activity Date"
              />
              <FormField
                id="createdFrom"
                label="Created From"
                type="date"
                value={searchFilters.createdFrom || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdFrom: e.target.value })
                }
              />
              <FormField
                id="createdTo"
                label="Created To"
                type="date"
                value={searchFilters.createdTo || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdTo: e.target.value })
                }
              />
            </div>
          )}

          <div className="flex  justify-between items-center">
            {roles?.includes('User') && availableYears.length > 0 && (
              <div className="flex self-start flex-wrap gap-2">
                {availableYears.map((year) => (
                  <Button
                    key={year}
                    variant={selectedYear === year ? 'primary' : 'secondary'}
                    onClick={() => setSelectedYear(year)}
                  >
                    {year}
                  </Button>
                ))}
              </div>
            )}
            <div className="space-x-4 mb-4">
              {showAdvanced && (
                <>
                  <Button variant="secondary" onClick={handleClearSearch}>
                    Clear Search
                  </Button>
                  <Button type="submit">Search</Button>
                </>
              )}

            </div>
            {!showAdvanced && roles?.includes('Admin') && (
              <GeneralSearch
                searchFilters={searchFilters}
                setSearchFilters={setSearchFilters}
              />
            )}
            {roles?.includes('User') && (
              <div className="hidden md:block">
                <ViewToggle view={view} toggle={toggle} />
              </div>
            )}
          </div>
        </form>

        <section>
          {/* keep your search form here */}


          {roles?.includes('Admin') || view === ViewModeConstants.TABLE ? (
            <ActivityTable
              data={areFiltersStale ? [] : items}
              handleViewMemberDetails={(dto) => handleOpenDrawer(dto, DrawerViewConstants.VIEW_MEMBER_DETAILS, 'Member Details', 'small')}
              handleEdit={(dto) => navigate(`/admin/activities/${dto.id}`)}
              handleDelete={handleDelete}
              handleSendEmail={handleSendEmail}
              handleViewAllEmails={handleViewAllEmails}
              handleViewRecordEmails={handleViewRecordEmails}
              handleRestore={handleRestore}
              handleApprove={(dto) =>
                handleOpenDrawer(dto, DrawerViewConstants.STATUS_CHANGE, 'Approve', 'large', ActivityStatusConstants.APPROVED)
              }
              handleChangeStatus={(dto) =>
                handleOpenDrawer(dto, DrawerViewConstants.STATUS_CHANGE, 'Change Status')
              }
              handleImpersonateMemberLogin={handleImpersonateMemberLogin}
              handleAddNote={(dto) =>
                handleOpenDrawer(dto, DrawerViewConstants.ADD_NOTE, 'Add Note')
              }
              handleViewNoteHistory={(dto) =>
                handleOpenDrawer(dto, DrawerViewConstants.VIEW_NOTE_HISTORY, 'Notes History')
              }
              handleMemberClick={handleMemberClick}
              currentPage={currentPage}
              totalPages={Math.ceil(totalRecords / pageSize)}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              isLoading={isLoading}
              sortBy={sortBy}
              sortDescending={sortDescending}
              onSort={handleSort}
              selectedIds={roles?.includes('Admin') ? selectedActivityIds : undefined}
              onSelectRow={roles?.includes('Admin') ? handleSelectRow : undefined}
              onSelectAll={roles?.includes('Admin') ? handleSelectAll : undefined}
            />
          ) : (
            <MemberActivityDataCard
              data={items}
              handleEdit={(dto) => navigate(`/admin/activities/${dto.id}`)}
              handleDelete={handleDelete}
              currentPage={currentPage}
              totalPages={Math.ceil(totalRecords / pageSize)}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              isLoading={isLoading}
            />
          )}
        </section>
      </Card>

      {viewingFile && (
        <FileViewer
          fileName={viewingFile.name}
          sasUrl={viewingFile.url}
          onClose={() => setViewingFile(null)}
        />
      )}

      <Drawer isOpen={isDrawerOpen} onClose={handleDrawerClose} title={drawerTite} size={drawerSize}>
        {drawerView === DrawerViewConstants.VIEW_MEMBER_DETAILS && (
          <MemberQuickDetails
            memberId={selectedActivity.member?.id ?? ''}
            onClose={handleDrawerClose}
          />
        )}
        {drawerView === DrawerViewConstants.FULL_HISTORY && member && <ComplianceHistoryFull member={member} />}
        {drawerView === DrawerViewConstants.STATUS_CHANGE && (
          <FeedbackForm
            key={drawerKey}
            componentId={ComponentConstants.ACTIVITIES}
            recordId={selectedActivity.id ?? ''}
            componentStatuses={activityStatusesData.map((status: ActivityStatusDTO) => ({
              value: status.id,
              label: status.title,
            }))}
            statusFieldName={statusFieldName}
            defaultStatusId={defaultStatusId}
            initialData={selectedActivity}
            onSubmit={handleStatusSubmit}
            onCancel={handleDrawerClose}
            updateLoading={isSubmitting}
          />
        )}
        {drawerView === DrawerViewConstants.ADD_NOTE && (
          <AddNote
            memberId={selectedActivity.member?.id || ''}
            componentId={ComponentConstants.ACTIVITIES}
            recordId={selectedActivity.id ?? ''}
            onSubmit={handleAddNote}
            onCancel={handleDrawerClose}
            isLoading={isSubmitting}
          />
        )}
        {drawerView === DrawerViewConstants.VIEW_NOTE_HISTORY && (
          <ViewNoteHistory memberId={selectedActivity.member?.id || ''} />
        )}
        <EmailDrawerContent
          drawerView={drawerView}
          selectedItem={selectedActivity}
          componentId={ComponentConstants.ACTIVITIES}
          getMemberId={(dto) => dto?.member?.id || ''}
          onEmailSubmit={handleSendEmailSubmit}
          onClose={handleDrawerClose}
          isSending={isSendingEmail}
        />
      </Drawer>
    </AdminLayout>
  );
};

export default ActivityList;
